import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { PlusCircle, X, MessageSquare, ListChecks, Sparkles, Loader2, ChevronLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  generateGeneralQuestions,
  generateSkillQuestions,
  generateAllSkillQuestions,
  generateScreeningQuestions
} from '@/services/openaiAssessmentService';

interface CustomAssessmentQuestionsStepProps {
  selectedSkills: string[];
  role: string;
  level: 'entry' | 'mid' | 'senior';
  onContinue: (generalQuestions: string[], skillQuestions: Record<string, string[]>) => void;
  onBack: () => void;
  initialGeneralQuestions?: string[];
  initialSkillQuestions?: Record<string, string[]>;
}

const CustomAssessmentQuestionsStep: React.FC<CustomAssessmentQuestionsStepProps> = ({
  selectedSkills,
  role,
  level,
  onContinue,
  onBack,
  initialGeneralQuestions = [''],
  initialSkillQuestions = {}
}) => {
  const [generalQuestions, setGeneralQuestions] = useState<string[]>(initialGeneralQuestions);
  const [skillQuestions, setSkillQuestions] = useState<Record<string, string[]>>(() => {
    // Initialize with the provided skill questions or empty arrays for each skill
    const initial: Record<string, string[]> = { ...initialSkillQuestions };
    
    // Make sure every selected skill has an entry
    selectedSkills.forEach(skill => {
      if (!initial[skill]) {
        initial[skill] = [''];
      }
    });
    
    return initial;
  });
  
  const [newGeneralQuestion, setNewGeneralQuestion] = useState('');
  const [activeSkillTab, setActiveSkillTab] = useState<string | 'general'>(
    selectedSkills.length > 0 ? selectedSkills[0] : 'general'
  );
  
  // States for AI suggestions
  const [isGeneratingGeneralSuggestions, setIsGeneratingGeneralSuggestions] = useState(false);
  const [isGeneratingSkillSuggestions, setIsGeneratingSkillSuggestions] = useState(false);
  const [generalSuggestions, setGeneralSuggestions] = useState<string[]>([]);
  const [skillSuggestions, setSkillSuggestions] = useState<Record<string, string[]>>({});

  const handleAddGeneralQuestion = () => {
    if (newGeneralQuestion.trim()) {
      setGeneralQuestions([...generalQuestions, newGeneralQuestion.trim()]);
      setNewGeneralQuestion('');
    }
  };

  const handleRemoveGeneralQuestion = (index: number) => {
    const updatedQuestions = [...generalQuestions];
    updatedQuestions.splice(index, 1);
    
    // Always keep at least one question
    if (updatedQuestions.length === 0) {
      updatedQuestions.push('');
    }
    
    setGeneralQuestions(updatedQuestions);
  };

  const handleUpdateGeneralQuestion = (index: number, value: string) => {
    const updatedQuestions = [...generalQuestions];
    updatedQuestions[index] = value;
    setGeneralQuestions(updatedQuestions);
  };

  const handleAddSkillQuestion = (skill: string, question: string) => {
    if (question.trim()) {
      setSkillQuestions(prev => ({
        ...prev,
        [skill]: [...(prev[skill] || []), question.trim()]
      }));
    }
  };

  const handleRemoveSkillQuestion = (skill: string, index: number) => {
    const updatedQuestions = [...(skillQuestions[skill] || [])];
    updatedQuestions.splice(index, 1);
    
    // Always keep at least one question for each skill
    if (updatedQuestions.length === 0) {
      updatedQuestions.push('');
    }
    
    setSkillQuestions(prev => ({
      ...prev,
      [skill]: updatedQuestions
    }));
  };

  const handleUpdateSkillQuestion = (skill: string, index: number, value: string) => {
    const updatedQuestions = [...(skillQuestions[skill] || [])];
    updatedQuestions[index] = value;
    
    setSkillQuestions(prev => ({
      ...prev,
      [skill]: updatedQuestions
    }));
  };

  const getSkillQuestionCount = (skill: string): number => {
    return (skillQuestions[skill] || []).filter(q => q.trim() !== '').length;
  };

  // New function to generate questions for all skills at once
  const handleGenerateAllSkillSuggestions = async () => {
    if (!role || selectedSkills.length === 0) {
      return;
    }
    
    setIsGeneratingSkillSuggestions(true);
    try {
      const allSkillQuestions = await generateAllSkillQuestions(role, level, selectedSkills);
      setSkillSuggestions(allSkillQuestions);
      
      // Use the new screening questions function for general questions
      const generalQuestions = await generateScreeningQuestions(role, level);
      setGeneralSuggestions(generalQuestions);
      
      // Show success toast
      toast.success("AI-generated questions ready! Click 'Add All' to use them.");
    } catch (error) {
      console.error("Error generating skill questions:", error);
      toast.error("Failed to generate AI questions. Using fallback options.");
    } finally {
      setIsGeneratingSkillSuggestions(false);
    }
  };

  // Function to generate general question suggestions using OpenAI
  const handleGenerateGeneralSuggestions = async () => {
    if (!role) {
      toast.error("Please provide a role title first");
      return;
    }
    
    setIsGeneratingGeneralSuggestions(true);
    toast.info("Generating AI-powered general questions...");
    try {
      // Use the new function for screening questions when it's the 'general' tab
      const suggestions = await generateScreeningQuestions(role, level);
      setGeneralSuggestions(suggestions);
      toast.success("AI-generated general questions ready!");
    } catch (error) {
      console.error("Error generating general question suggestions:", error);
      toast.error("Failed to generate AI questions. Using fallback options.");
    } finally {
      setIsGeneratingGeneralSuggestions(false);
    }
  };

  // Function to generate skill-specific questions using OpenAI
  const handleGenerateSkillSuggestions = async () => {
    if (!role || !activeSkillTab || activeSkillTab === 'general') {
      toast.error("Please select a skill first");
      return;
    }
    
    setIsGeneratingSkillSuggestions(true);
    toast.info(`Generating AI-powered questions for ${activeSkillTab}...`);
    try {
      const suggestions = await generateSkillQuestions(role, level, activeSkillTab);
      setSkillSuggestions(prev => ({
        ...prev,
        [activeSkillTab]: suggestions
      }));
      toast.success(`AI-generated ${activeSkillTab} questions ready!`);
    } catch (error) {
      console.error(`Error generating ${activeSkillTab} question suggestions:`, error);
      toast.error("Failed to generate AI questions. Using fallback options.");
    } finally {
      setIsGeneratingSkillSuggestions(false);
    }
  };

  const handleAddGeneralSuggestion = (suggestion: string) => {
    setGeneralQuestions(prev => [...prev.filter(q => q.trim() !== ''), suggestion]);
    // Remove the suggestion from the list to avoid duplicates
    setGeneralSuggestions(prev => prev.filter(s => s !== suggestion));
  };

  const handleAddSkillSuggestion = (skill: string, suggestion: string) => {
    setSkillQuestions(prev => ({
      ...prev,
      [skill]: [...prev[skill].filter(q => q.trim() !== ''), suggestion]
    }));
    // Remove the suggestion from the list to avoid duplicates
    setSkillSuggestions(prev => ({
      ...prev,
      [skill]: (prev[skill] || []).filter(s => s !== suggestion)
    }));
  };

  // Add function to add all suggestions at once
  const handleAddAllSuggestions = () => {
    if (activeSkillTab === 'general') {
      // Add all general suggestions
      if (generalSuggestions.length > 0) {
        const nonEmptyQuestions = generalQuestions.filter(q => q.trim() !== '');
        const uniqueSuggestions = generalSuggestions.filter(
          suggestion => !nonEmptyQuestions.includes(suggestion)
        );
        
        setGeneralQuestions([...nonEmptyQuestions, ...uniqueSuggestions]);
        setGeneralSuggestions([]); // Clear suggestions after adding
        toast.success("Added all 3 general question suggestions");
      }
    } else if (activeSkillTab && skillSuggestions[activeSkillTab]?.length > 0) {
      // Add all skill-specific suggestions
      const currentSkillQuestions = skillQuestions[activeSkillTab] || [];
      const nonEmptyQuestions = currentSkillQuestions.filter(q => q.trim() !== '');
      const uniqueSuggestions = skillSuggestions[activeSkillTab].filter(
        suggestion => !nonEmptyQuestions.includes(suggestion)
      );
      
      setSkillQuestions(prev => ({
        ...prev,
        [activeSkillTab]: [...nonEmptyQuestions, ...uniqueSuggestions]
      }));
      
      // Clear the suggestions for this skill
      setSkillSuggestions(prev => ({
        ...prev,
        [activeSkillTab]: []
      }));
      
      toast.success(`Added all 3 ${activeSkillTab} question suggestions`);
    }
  };

  const handleContinue = () => {
    // Filter out empty questions
    const filteredGeneralQuestions = generalQuestions.filter(q => q.trim() !== '');
    
    // Filter out empty questions from each skill
    const filteredSkillQuestions: Record<string, string[]> = {};
    Object.entries(skillQuestions).forEach(([skill, questions]) => {
      filteredSkillQuestions[skill] = questions.filter(q => q.trim() !== '');
    });
    
    // Check if we have at least one question in total
    const totalQuestions = [
      ...filteredGeneralQuestions,
      ...Object.values(filteredSkillQuestions).flat()
    ];
    
    if (totalQuestions.length === 0) {
      toast.error("Please add at least one question");
      return;
    }
    
    onContinue(filteredGeneralQuestions, filteredSkillQuestions);
  };

  // Function to determine if a skill has questions
  const hasQuestions = (skill: string): boolean => {
    return getSkillQuestionCount(skill) > 0;
  };

  // Check if general questions have been added
  const hasGeneralQuestions = (): boolean => {
    return generalQuestions.filter(q => q.trim() !== '').length > 0;
  };

  // Function to check if all skills have at least one question
  const allSkillsHaveQuestions = (): boolean => {
    return selectedSkills.every(skill => hasQuestions(skill));
  };

  // Function to get styling for tab based on question status
  const getTabStyling = (isActive: boolean, hasContent: boolean) => {
    if (isActive) return 'bg-[#9b87f5] text-white';
    if (hasContent) return 'bg-[#6fc276] text-white';
    return 'bg-[#F8F7FF] text-[#1A1F2C] hover:bg-[#9b87f5]/20';
  };

  return (
    <div className="bg-white/90 rounded-lg border border-[#9b87f5]/30 shadow-lg w-full max-w-3xl px-8 py-10 backdrop-blur-sm">
      <h1 className="text-3xl font-bold text-center mb-3 text-[#1A1F2C]">Customize Your Assessment</h1>
      
      <p className="text-center text-[#1A1F2C]/80 mb-8">
        Configure custom questions for {level} {role}
      </p>
      
      {/* Top level category tabs for navigation */}
      <div className="border-b border-[#9b87f5]/20 mb-6">
        <div className="flex overflow-x-auto gap-2">
          {selectedSkills.map((skill) => (
            <button 
              key={skill}
              onClick={() => setActiveSkillTab(skill)}
              className={`px-5 py-2 text-sm font-medium ${
                activeSkillTab === skill 
                  ? 'text-white bg-[#9b87f5]' 
                  : hasQuestions(skill) 
                    ? 'bg-[#6fc276] text-white' 
                    : 'bg-[#F8F7FF] text-[#1A1F2C] hover:bg-[#9b87f5]/20'
              } rounded-t-lg flex items-center`}
            >
              {skill} ({getSkillQuestionCount(skill)})
            </button>
          ))}
          <button 
            onClick={() => setActiveSkillTab('general')}
            className={`px-5 py-2 text-sm font-medium ${
              activeSkillTab === 'general' 
                ? 'text-white bg-[#9b87f5]' 
                : hasGeneralQuestions() 
                  ? 'bg-[#6fc276] text-white' 
                  : 'bg-[#F8F7FF] text-[#1A1F2C]'
            } rounded-t-lg flex items-center`}
          >
            General Questions ({generalQuestions.filter(q => q.trim() !== '').length})
          </button>
        </div>
      </div>
      
      <div className="space-y-8">
        {/* Questions Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {activeSkillTab === 'general' ? (
                <>
                  <MessageSquare className="text-[#9b87f5] mr-2" size={20} />
                  <h2 className="text-xl font-semibold text-[#1A1F2C]">General Assessment Questions</h2>
                </>
              ) : (
                <>
                  <ListChecks className="text-[#9b87f5] mr-2" size={20} />
                  <h2 className="text-xl font-semibold text-[#1A1F2C]">Questions for {activeSkillTab}</h2>
                </>
              )}
            </div>
            
            {/* AI suggestion button */}
            <Button
              variant="outline"
              size="sm"
              className="bg-[#F8F7FF] text-[#9b87f5] border-[#9b87f5]/30 hover:bg-[#9b87f5]/20 hover:text-[#9b87f5] transition-colors duration-200"
              onClick={activeSkillTab === 'general' ? handleGenerateGeneralSuggestions : handleGenerateSkillSuggestions}
              disabled={
                (activeSkillTab === 'general' && isGeneratingGeneralSuggestions) || 
                (activeSkillTab !== 'general' && isGeneratingSkillSuggestions)
              }
            >
              {((activeSkillTab === 'general' && isGeneratingGeneralSuggestions) || 
                (activeSkillTab !== 'general' && isGeneratingSkillSuggestions)) ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles size={16} className="mr-2" />
                  Suggest Questions
                </>
              )}
            </Button>
          </div>
          
          {activeSkillTab === 'general' ? (
            // General Questions
            <div className="space-y-3">
              {/* AI Suggestions for General Questions */}
              {generalSuggestions.length > 0 && (
                <div className="bg-[#F8F7FF] rounded-lg p-4 border border-[#9b87f5]/20 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <Sparkles className="text-[#9b87f5] mr-2" size={16} />
                      <h3 className="text-sm font-medium text-[#1A1F2C]">AI-Generated Questions</h3>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-[#F8F7FF] text-[#9b87f5] border-[#9b87f5]/30 hover:bg-[#9b87f5]/20 hover:text-[#9b87f5] transition-colors duration-200 text-xs"
                      onClick={handleAddAllSuggestions}
                    >
                      Add All
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {generalSuggestions.map((suggestion, index) => (
                      <div key={index} className="flex items-center justify-between bg-white rounded p-2 border border-[#9b87f5]/10">
                        <p className="text-sm text-[#1A1F2C] mr-2">{suggestion}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#9b87f5] hover:bg-[#9b87f5]/20 transition-colors duration-200 whitespace-nowrap"
                          onClick={() => handleAddGeneralSuggestion(suggestion)}
                        >
                          <PlusCircle size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {generalQuestions.map((question, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Textarea
                    value={question}
                    onChange={(e) => handleUpdateGeneralQuestion(index, e.target.value)}
                    placeholder="Enter a general question (e.g., work experience, availability)..."
                    className="flex-1 border-[#9b87f5]/30 focus-visible:ring-[#9b87f5]/50"
                    rows={2}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveGeneralQuestion(index)}
                    disabled={generalQuestions.length === 1 && !question}
                    className="mt-2 text-gray-400 hover:text-red-500"
                  >
                    <X size={16} />
                  </Button>
                </div>
              ))}
              
              <div className="flex items-start gap-2">
                <Textarea
                  value={newGeneralQuestion}
                  onChange={(e) => setNewGeneralQuestion(e.target.value)}
                  placeholder="Type a new general question..."
                  className="flex-1 border-[#9b87f5]/30 focus-visible:ring-[#9b87f5]/50"
                  rows={2}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleAddGeneralQuestion}
                  disabled={!newGeneralQuestion.trim()}
                  className="mt-2 bg-[#F8F7FF] text-[#9b87f5] border-[#9b87f5]/30 hover:bg-[#9b87f5]/20 hover:text-[#9b87f5] transition-colors duration-200"
                >
                  <PlusCircle size={16} />
                </Button>
              </div>
              
              <div className="text-sm text-[#1A1F2C]/60 mt-2">
                <p>Suggested general questions: salary expectations, notice period, work preferences...</p>
              </div>
            </div>
          ) : (
            // Skill-specific Questions
            <div className="space-y-3">
              {/* AI Suggestions for Skill Questions */}
              {activeSkillTab !== 'general' && skillSuggestions[activeSkillTab]?.length > 0 && (
                <div className="bg-[#F8F7FF] rounded-lg p-4 border border-[#9b87f5]/20 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <Sparkles className="text-[#9b87f5] mr-2" size={16} />
                      <h3 className="text-sm font-medium text-[#1A1F2C]">AI-Generated {activeSkillTab} Questions</h3>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-[#F8F7FF] text-[#9b87f5] border-[#9b87f5]/30 hover:bg-[#9b87f5]/20 hover:text-[#9b87f5] transition-colors duration-200 text-xs"
                      onClick={handleAddAllSuggestions}
                    >
                      Add All
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {skillSuggestions[activeSkillTab].map((suggestion, index) => (
                      <div key={index} className="flex items-center justify-between bg-white rounded p-2 border border-[#9b87f5]/10">
                        <p className="text-sm text-[#1A1F2C] mr-2">{suggestion}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#9b87f5] hover:bg-[#9b87f5]/20 transition-colors duration-200 whitespace-nowrap"
                          onClick={() => handleAddSkillSuggestion(activeSkillTab, suggestion)}
                        >
                          <PlusCircle size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {activeSkillTab && skillQuestions[activeSkillTab]?.map((question, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Textarea
                    value={question}
                    onChange={(e) => handleUpdateSkillQuestion(activeSkillTab, index, e.target.value)}
                    placeholder={`Enter a question about ${activeSkillTab}...`}
                    className="flex-1 border-[#9b87f5]/30 focus-visible:ring-[#9b87f5]/50"
                    rows={2}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveSkillQuestion(activeSkillTab, index)}
                    disabled={(skillQuestions[activeSkillTab]?.length === 1 && !question) || !activeSkillTab}
                    className="mt-2 text-gray-400 hover:text-red-500"
                  >
                    <X size={16} />
                  </Button>
                </div>
              ))}
              
              <div className="flex items-start gap-2">
                <Textarea
                  placeholder={`Type a new question about ${activeSkillTab}...`}
                  className="flex-1 border-[#9b87f5]/30 focus-visible:ring-[#9b87f5]/50"
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && activeSkillTab !== 'general') {
                      e.preventDefault();
                      handleAddSkillQuestion(activeSkillTab, e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={(e) => {
                    if (activeSkillTab !== 'general') {
                      const textarea = e.currentTarget.previousSibling as HTMLTextAreaElement;
                      if (textarea && textarea.value.trim()) {
                        handleAddSkillQuestion(activeSkillTab, textarea.value);
                        textarea.value = '';
                      }
                    }
                  }}
                  disabled={activeSkillTab === 'general'}
                  className="mt-2 bg-[#F8F7FF] text-[#9b87f5] border-[#9b87f5]/30 hover:bg-[#9b87f5]/20 hover:text-[#9b87f5] transition-colors duration-200"
                >
                  <PlusCircle size={16} />
                </Button>
              </div>
              
              <div className="text-sm text-[#1A1F2C]/60 mt-2">
                <p>Examples: technical challenges faced, projects involving {activeSkillTab}, specific tools used...</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-between mt-10">
          <Button 
            variant="outline" 
            className="border-[#9b87f5]/30 bg-white hover:bg-[#9b87f5]/10 text-[#1A1F2C]/70 hover:text-[#1A1F2C]"
            onClick={onBack}
          >
            Back
          </Button>
          <Button 
            variant="purple"
            className="px-8 py-6 bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA] shadow-md"
            onClick={handleContinue}
            disabled={!allSkillsHaveQuestions()}
          >
            {allSkillsHaveQuestions() 
              ? "Continue to Settings" 
              : `Add questions to all ${selectedSkills.length} skills to continue`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomAssessmentQuestionsStep; 